'use client';

import { useEffect, useRef } from 'react';
import Image from 'next/image';

/**
 * Hero product visual (desktop only — the parent hides it on mobile).
 * The real backoffice screenshot tilted in 3D, coming from back (tilted) to
 * front (flat) as you scroll: JS sets --enter from scrollY, CSS `.hero-tilt`
 * maps it. Reduced-motion = fixed gentle tilt. Theme-aware.
 */
export function HeroShowcase() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const enter = Math.min(1, Math.max(0, window.scrollY / 420));
      el.style.setProperty('--enter', String(enter));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);

      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="relative w-full [perspective:2000px]">
      {/* Brand glow so the screenshot floats with its own light — dark only.
          Wider than the card so it haloes out past the edges instead of hiding
          behind it. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-22%] hidden h-[55%] w-[115%] -translate-x-1/2 rounded-[50%] opacity-100 blur-[90px] dark:block"
        style={{
          background: 'radial-gradient(closest-side, var(--color-brand-on-dark), transparent 72%)',
        }}
      />

      <div ref={ref} className="hero-tilt relative mx-auto w-full max-w-6xl">
        <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-[var(--shadow-lg)]">
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <span className="size-2.5 rounded-full bg-[#ff5f57]" />
            <span className="size-2.5 rounded-full bg-[#febc2e]" />
            <span className="size-2.5 rounded-full bg-[#28c840]" />
            <span className="ml-3 truncate rounded-md bg-paper px-3 py-1 font-mono text-xs text-ink-soft">
              backoffice.mendly.com.ar
            </span>
          </div>
          <div className="relative aspect-[1738/900] w-full">
            <Image
              src="/screens/2.png"
              alt="Panel de Mendly con las reparaciones del taller"
              fill
              priority
              sizes="1152px"
              className="object-cover object-top dark:hidden"
            />
            <Image
              src="/screens/3.png"
              alt="Panel de Mendly con las reparaciones del taller"
              fill
              priority
              sizes="1152px"
              className="hidden object-cover object-top dark:block"
            />
          </div>
        </div>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-paper to-transparent"
      />
    </div>
  );
}
